import { useMemo, useRef, useState } from 'react';

import { uploadGroupFile } from '@src/api/file';
import CustomTag from '@src/components/Tag';
import useGetMembers from '@src/hooks/queries/member/useGetMembers';
import { openModal } from '@src/reduce/modals';
import getIsResponseFalse from '@src/utils/getIsResponseFalse';
import { Button, DatePicker, Input, Select, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import { ReactComponent as DateIcon } from '@assets/icons/date-suffix.svg';
import { ReactComponent as MapIcon } from '@assets/icons/map.svg';
import { ReactComponent as PlusIcon } from '@assets/icons/plus.svg';
import { ReactComponent as UploadIcon } from '@assets/icons/upload.svg';

import { FormProps } from '../type';
import * as S from './styled';

const actionTypeOptions = [
  { value: 'homepage', label: '홈페이지 게시' },
  { value: 'band', label: '밴드 게시' },
  { value: 'mail', label: '메일 발송' },
];

const contactFormat = /^.+\(\d{2,3}-\d{3,4}-\d{4}\)$/;

const FormOpening = ({
  formData,
  setFormData,
  setTitle,
  hasInitialFormData,
  isEditable,
  initialFileName,
  approveStatus,
  licenseNosMember,
}: FormProps) => {
  const {
    licenseNos,
    targetName,
    date,
    address,
    detailAddress,
    contacts,
    remarks,
    paymentAccount,
    actionType,
    link,
  } = formData;

  const dispatch = useDispatch();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const [selectedLicenseNosMember, setSelectedLicenseNosMember] = useState<
    { name: string; licenseCb: number | null }[]
  >([]);
  const [memberSearchName, setMemberSearchName] = useState<string>();
  const [contact, setContact] = useState<string>('');
  const [fileName, setFileName] = useState<string>(initialFileName ?? '');

  const processedTitle = useMemo(() => {
    if (!memberSearchName) {
      return '';
    }

    let processed = memberSearchName.replaceAll(/[ㄱ-ㅎ]/g, '');

    return processed;
  }, [memberSearchName]);

  const { data } = useGetMembers({
    page: 1,
    limit: processedTitle ? 20 : 100,
    q: processedTitle ? processedTitle : undefined,
    searchable: false,
  });

  const eventName = useMemo(() => {
    if (licenseNos.length === 0) return '';

    const userInfo =
      (selectedLicenseNosMember.length ? selectedLicenseNosMember : undefined) ||
      data.concat(licenseNosMember).filter(({ licenseNo }) => licenseNos.includes(licenseNo));

    if (userInfo.length === 0) return '';

    let memberNames: string[] = [];
    userInfo.forEach(({ name, licenseCb }) => {
      memberNames.push(`${name}/${licenseCb ? licenseCb + '기' : '경력'}`);
    });

    const result = `[개업] ${targetName} (${memberNames.join(', ')}) 사무소 개업 알림!!`;

    setTitle(result);

    return result;
  }, [licenseNos, targetName, licenseNosMember, selectedLicenseNosMember]);

  const isContactValid = useMemo(() => {
    if (contactFormat.test(contact) || !contact) {
      return true;
    }
    return false;
  }, [contact]);

  const options = useMemo(() => {
    const result = data
      .concat(licenseNosMember)
      .map(({ name, licenseCb, licenseNo, officeName }) => ({
        label: `${name}(${licenseCb ? licenseCb + '기' : '경력'}${
          officeName ? '/' + officeName : ''
        })`,
        value: licenseNo,
      }));

    return result;
  }, [data, licenseNosMember]);

  const addContact = () => {
    if (!contact) {
      message.error('연락처를 입력해주세요.');
      return;
    }
    if (!isContactValid) {
      message.error('연락처 형식이 올바르지 않습니다.');
      return;
    }

    const contactInfo = {
      name: contact.split('(')[0],
      phone: contact.split('(')[1].split(')')[0],
    };

    setContact('');
    setFormData({ ...formData, contacts: [...contacts, contactInfo] });
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files;
    console.log({ e, inputFiles });
    if (!inputFiles?.length) return;

    const formData = new FormData();
    formData.append('file', inputFiles[0]);

    const response = await uploadGroupFile({ data: formData });

    if (getIsResponseFalse(response)) return;

    setFileName(inputFiles[0].name);
    setFormData(prev => ({ ...prev, fileIds: [response] }));
  };

  const disabled = hasInitialFormData && !isEditable;
  const baseInfoDisabled = hasInitialFormData && !(isEditable && approveStatus === 'pending');

  return (
    <S.Container>
      <S.Item>
        <p>
          개업장명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Input
          disabled={baseInfoDisabled}
          className='right_content'
          placeholder='개업장명을 입력하세요.'
          value={targetName}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, targetName: value });
          }}
        />
      </S.Item>
      <S.Item>
        <p>
          회원명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Select
          className='right_content'
          mode='multiple'
          disabled={baseInfoDisabled}
          tagRender={CustomTag}
          showSearch
          value={licenseNos.length === 0 ? undefined : licenseNos}
          options={options}
          filterOption={false}
          onSearch={e => setMemberSearchName(e)}
          onChange={(e, o) => {
            const getMemberByLabel = (label: string) => {
              const licenseCb = label.includes('경력')
                ? null
                : Number(label.split('기')[0].split('(')[1]);
              const name = label.split('(')[0];
              return { licenseCb, name };
            };

            setSelectedLicenseNosMember(
              Array.isArray(o)
                ? o.map(({ label }) => getMemberByLabel(label))
                : [getMemberByLabel(o.label)],
            );
            setFormData({ ...formData, licenseNos: e });
          }}
          placeholder='회원을 선택해주세요.(복수 선택 가능)'
          suffixIcon={false}></Select>
      </S.Item>
      <S.Item>
        <p>
          경조명<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Input className='right_content' disabled value={eventName} />
      </S.Item>
      <S.Item>
        <p>
          개업 일시<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <DatePicker
          disabled={disabled}
          className='right_content'
          format='YYYY-MM-DD HH:mm'
          showTime={{ format: 'HH:mm' }}
          value={date ? dayjs(date) : undefined}
          showNow={false}
          placement={'bottomRight'}
          placeholder='날짜를 선택해주세요.'
          suffixIcon={<DateIcon />}
          onChange={e => {
            setFormData({ ...formData, date: dayjs(e).format('YYYY-MM-DD HH:mm') });
          }}
        />
      </S.Item>
      <S.Items>
        <p>
          장소<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <div className='right_content'>
          <Input
            disabled={disabled}
            value={address}
            placeholder='장소를 입력하세요.'
            onFocus={() => {
              dispatch(openModal({ name: 'postcodeModal', props: { setFormData } }));
            }}
            suffix={<MapIcon />}
            style={{ borderRadius: '16px', marginBottom: '16px' }}
          />
          <Input
            disabled={disabled}
            placeholder='상세주소를 입력하세요.'
            value={detailAddress}
            onChange={({ target: { value } }) => {
              setFormData({ ...formData, detailAddress: value });
            }}
          />
        </div>
      </S.Items>
      <S.Items
        style={{
          alignItems: isContactValid && contacts.length < 1 ? 'center' : 'unset',
        }}>
        <p>회원 연락처</p>
        <div className='right_content'>
          {contacts.map(({ name, phone }, index) => (
            <Input
              key={index}
              value={`${name}(${phone})`}
              disabled
              style={{ marginBottom: '16px' }}
            />
          ))}
          <div style={{ display: 'flex' }}>
            <Input
              disabled={disabled}
              value={contact}
              onChange={({ target: { value } }) => {
                setContact(value);
              }}
              placeholder='이름(010-1234-1234)'
            />
            <Button
              disabled={disabled}
              style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}
              onClick={addContact}>
              <PlusIcon />
              <span style={{ marginLeft: '8px' }}>연락처 추가</span>
            </Button>
          </div>

          {!isContactValid && (
            <div style={{ color: '#F60303', marginTop: '8px' }}>
              '이름(010-1234-1234)' 형식만 입력 가능합니다.
            </div>
          )}
        </div>
      </S.Items>
      <S.Items>
        <p>인사의 말씀</p>
        <TextArea
          disabled={disabled}
          className='right_content'
          rows={4}
          value={remarks}
          placeholder='인사의 말씀'
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, remarks: value });
          }}
        />
      </S.Items>
      <S.Item>
        <p>계좌</p>
        <Input
          disabled={disabled}
          className='right_content'
          value={paymentAccount}
          placeholder='계좌(은행명, 계좌번호, 예금주명)를 입력하세요.'
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, paymentAccount: value });
          }}
        />
      </S.Item>
      <S.Item>
        <p>첨부파일</p>
        <div
          className='right_content'
          onClick={() => {
            if (!disabled) {
              inputFileRef.current?.click();
            }
          }}>
          <Input
            placeholder='함께 공지 원하시는 첨부파일 별첨(안내장 등)'
            disabled={disabled}
            suffix={<UploadIcon />}
            style={{ borderRadius: '16px' }}
            value={fileName}
          />
          <input ref={inputFileRef} type='file' style={{ display: 'none' }} onChange={uploadFile} />
        </div>
      </S.Item>
      <S.Item>
        <p>링크</p>
        <Input
          className='right_content'
          placeholder='링크를 입력하세요.'
          disabled={disabled}
          value={link}
          onChange={({ target: { value } }) => {
            setFormData({ ...formData, link: value });
          }}
          style={{ borderRadius: '16px' }}
        />
      </S.Item>
      <S.Item>
        <p>
          조치 종류<span style={{ color: '#FF0000' }}>*</span>
        </p>
        <Select
          placeholder='조치 종류를 선택하세요.(복수선택 가능)'
          disabled={disabled || approveStatus === 'approved'}
          mode='multiple'
          className='right_content'
          tagRender={CustomTag}
          options={actionTypeOptions}
          value={actionType}
          onChange={e => {
            setFormData({ ...formData, actionType: e });
          }}
        />
      </S.Item>
    </S.Container>
  );
};

export default FormOpening;
